import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarAteDuasCasas, formatarDataHora, interpretarNumero } from '@/lib/formato';
import type { ContextoDoRetorno } from '@/lib/retorno';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import type { MovimentoDaApi } from './api';
import { rotuloDoLancamento } from './ajuste';
import { ID_DO_LIVRO } from './livro-do-tanque';
import { useEstornarMovimento, useExcluirMovimento, useMovimentosDoTanque, usePedirCorrecao } from './consultas';

/**
 * O LIVRO DE UM TANQUE — o que entrou, o que saiu, e o que já foi corrigido.
 *
 * Ela nasceu junto com o estorno, e por causa dele. Até aqui o saldo do tanque
 * era um número sem como abrir: quem visse 19.000 L num tanque de 2.000 não
 * tinha por onde descobrir QUAL lançamento estava errado — e corrigir sem
 * poder apontar a linha não é corrigir, é adivinhar.
 *
 * ## Corrigir é PEDIR (0059)
 *
 * Quem lançou errado pede a correção com o valor certo e o motivo; o livro só
 * muda quando alguém com alçada de ajuste aprova, em "Pedidos de ajuste". A
 * linha que nasce da aprovação diz quem pediu, quem aprovou e quando.
 *
 * O estorno imediato da ADR-008 continua, em "Mais ações", para quem tem a
 * licença dele. Excluir é só da administração da plataforma, pelo acesso de
 * suporte — nenhum papel da empresa vê essa ação.
 *
 * ## Por que nenhuma destas ações é laranja
 *
 * O manual dá UMA ação em laranja sólido por tela, e nesta ela já é do hub.
 * Corrigir é a exceção, não o caminho do dia; pintá-la da mesma cor convidaria
 * a usá-la. A confirmação é que nomeia o que vai acontecer.
 */
export function LivroDoTanque({ tanqueId, tanqueNome }: {
  readonly tanqueId: string;
  readonly tanqueNome: string;
}) {
  const consulta = useMovimentosDoTanque(tanqueId);
  const estornar = useEstornarMovimento();
  const corrigir = usePedirCorrecao();
  const excluir = useExcluirMovimento();
  const permissoes = useSessaoEmMemoria()?.permissoes ?? null;

  /* Em quê e o que valeu, para o retorno (R04, p.32): o tanque e a linha do livro. */
  function contextoDoLancamento(m: MovimentoDaApi): ContextoDoRetorno {
    return {
      alvo: { nome: tanqueNome },
      fatos: [`${rotuloDoLancamento(m.tipo)} de ${formatarAteDuasCasas(Number(m.quantidadeL))} L`, formatarDataHora(new Date(m.ocorridoEm))],
    };
  }

  /* `null` significa "a sessão não diz" — e aí o botão aparece, porque a rota
     confere de qualquer forma. Esconder por falta de informação deixaria quem
     PODE sem o caminho, que é pior do que mostrar um botão que responde 403.
     A exclusão é a exceção: ela é da plataforma, e na dúvida não aparece. */
  const podeEstornar = permissoes === null || permissoes.includes('movimento_tanque.estornar');
  const podeCorrigir = permissoes === null || permissoes.includes('movimento_tanque.corrigir');
  const podeExcluir = permissoes !== null && permissoes.includes('movimento_tanque.excluir');

  const [acao, setAcao] = useState<'estorno' | 'correcao' | 'exclusao' | null>(null);
  const [alvo, setAlvo] = useState<MovimentoDaApi | null>(null);
  const [motivo, setMotivo] = useState('');
  const [quantidade, setQuantidade] = useState('');

  const movimentos = consulta.data ?? [];
  const paginaDoLivro = usePaginacao(movimentos);
  const curto = motivo.trim().length < 15;
  const correta = interpretarNumero(quantidade);
  const correcaoValida = correta !== null && correta >= 0 && alvo !== null && correta !== Number(alvo.quantidadeL);

  function abrir(qual: 'estorno' | 'correcao' | 'exclusao', m: MovimentoDaApi) {
    setAcao(qual);
    setAlvo(m);
    setMotivo('');
    setQuantidade('');
  }

  function fechar() {
    setAcao(null);
    setAlvo(null);
    setMotivo('');
    setQuantidade('');
    estornar.reset();
    corrigir.reset();
    excluir.reset();
  }

  const COLUNAS: readonly Coluna<MovimentoDaApi>[] = [
    {
      chave: 'quando',
      rotulo: 'Quando',
      prioridade: 'chave',
      celula: (m) => formatarDataHora(new Date(m.ocorridoEm)),
    },
    {
      chave: 'tipo',
      rotulo: 'Lançamento',
      celula: (m) => (
        <div className="min-w-0">
          <div>{m.ajusteTipo === 'balanco' ? `Balanço · ${rotuloDoLancamento(m.tipo).toLowerCase()}` : rotuloDoLancamento(m.tipo)}</div>
          {m.outroTanque !== null ? (
            /* 0062: a transferência diz o outro tanque e o caminhão. */
            <div className="txt-linha2 block text-tinta-secundaria">
              {[m.tipo === 'transferencia_saida' ? `para ${m.outroTanque}` : `de ${m.outroTanque}`, m.caminhao ? `caminhão ${m.caminhao}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          ) : m.fornecedor === null && m.documento === null && m.observacao === null ? null : (
            <div className="txt-linha2 block text-tinta-secundaria">
              {[m.fornecedor, m.documento, m.observacao].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    {
      chave: 'quantidade',
      rotulo: 'Quantidade',
      alinhamento: 'numero',
      celula: (m) => (
        <span className="tabular-nums">
          {m.sentido > 0 ? '+' : '−'}
          {formatarAteDuasCasas(Number(m.quantidadeL))} L
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      /* Rótulo escrito, nunca só a cor (manual, p.09). A linha de um ajuste
         aprovado carrega o registro dele: quem pediu, quem aprovou, e quando. */
      celula: (m) => {
        if (m.ajusteTipo !== null) {
          return (
            <div className="min-w-0">
              <div>{m.ajusteTipo === 'balanco' ? 'Balanço aprovado' : 'Correção aprovada'}</div>
              <div className="txt-linha2 block text-tinta-secundaria">
                {`Pedido por ${m.ajusteSolicitadoPor ?? '—'}${m.ajusteSolicitadoEm ? ` em ${formatarDataHora(new Date(m.ajusteSolicitadoEm))}` : ''} · aprovado por ${m.ajusteAprovadoPor ?? '—'}${m.ajusteAprovadoEm ? ` em ${formatarDataHora(new Date(m.ajusteAprovadoEm))}` : ''}`}
              </div>
            </div>
          );
        }
        if (m.transferenciaSituacao !== null && m.estornadoEm === null) {
          /* 0062: a viagem inteira — o que saiu, o que chegou, a perda e quem
             registrou cada etapa —, nos dois tanques. */
          if (m.transferenciaSituacao === 'em_transito') {
            return (
              <div className="min-w-0">
                <div>Em trânsito</div>
                <div className="txt-linha2 block text-tinta-secundaria">
                  {`saída por ${m.transferenciaSaidaPor ?? '—'}; a chegada ainda não foi registrada`}
                </div>
              </div>
            );
          }
          const perda = m.transferenciaPerdaL ?? 0;
          return (
            <div className="min-w-0">
              <div>{perda > 0 ? `Chegou com perda de ${formatarAteDuasCasas(perda)} L` : 'Chegou inteira'}</div>
              <div className="txt-linha2 block text-tinta-secundaria">
                {[
                  `saíram ${formatarAteDuasCasas(m.transferenciaSaidaL ?? 0)} L, chegaram ${formatarAteDuasCasas(m.transferenciaChegadaL ?? 0)} L`,
                  perda > 0 && m.transferenciaJustificativa ? m.transferenciaJustificativa : null,
                  `saída por ${m.transferenciaSaidaPor ?? '—'}, chegada por ${m.transferenciaChegadaPor ?? '—'}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          );
        }
        if (m.ehContraLancamento) return <span className="text-tinta-secundaria">Correção de outro lançamento</span>;
        if (m.temCorrecaoPendente) return <span className="text-tinta-secundaria">Correção aguardando aprovação</span>;
        if (m.estornadoEm === null) return <span className="text-tinta-secundaria">Lançado</span>;
        return (
          <div className="min-w-0">
            <div>Estornado</div>
            <div className="txt-linha2 block text-tinta-secundaria">{m.motivoDoEstorno}</div>
          </div>
        );
      },
    },
  ];

  return (
    <section className="flex flex-col gap-4" aria-labelledby={ID_DO_LIVRO}>
      <div>
        {/* `-1`: recebe o foco quando a linha do tanque e escolhida
            (`mostrarLivroDoTanque`), sem entrar na ordem do Tab. */}
        <h2 id={ID_DO_LIVRO} tabIndex={-1} className="txt-secao scroll-mt-6 text-tinta">
          Lançamentos de {tanqueNome}
        </h2>
        <p className="text-tinta-secundaria">
          Do mais recente para o mais antigo. Corrigir um lançamento é pedir: o volume só muda quando alguém com
          alçada de ajuste aprova, e a linha errada continua na lista, anulada, ao lado da certa.
        </p>
      </div>

      <Tabela
        rotulo={`Lançamentos de ${tanqueNome}`}
        colunas={COLUNAS}
        linhas={paginaDoLivro.visiveis}
        rodape={<RodapeTabela {...paginaDoLivro.rodape} />}
        chaveDaLinha={(m) => m.id}
        carregando={consulta.isPending}
        /* A ACAO DA LINHA (p.18): ultima celula, a direita, a vista no hover e
           no foco — e fora da conta das oito colunas. */
        acoes={(m) => {
          if (m.estornadoEm !== null || m.ehContraLancamento || m.ajusteTipo !== null) return null;
          /* O motivo de nao poder vai ESCRITO no lugar do botao: um espaco em
             branco faria a pessoa procurar o botao que nunca existiu ali. */
          if (m.deAbastecimento) {
            return <span className="txt-apoio text-tinta-apagada">Corrija pelo abastecimento</span>;
          }
          if (m.temCorrecaoPendente) {
            return <span className="txt-apoio text-tinta-apagada">Aguardando aprovação</span>;
          }
          /* A transferência em duas etapas se acerta pelo balanço (0062): a
             correção refaria o par e apagaria a perda do histórico. */
          if (m.transferenciaSituacao !== null) {
            return <span className="txt-apoio text-tinta-apagada">Acerte pelo balanço</span>;
          }
          if (!podeCorrigir) return null;
          return <Botao nivel="terciario" onClick={() => { abrir('correcao', m); }}>Corrigir</Botao>;
        }}
        /* Os tres pontos, fixos no canto direito (pedido do dono, 03/10/2026):
           o estorno imediato e a exclusao, que sao excecao, ficam aqui. */
        menu={(m) => {
          if (m.estornadoEm !== null || m.ehContraLancamento || m.ajusteTipo !== null) return null;
          if (m.deAbastecimento || m.temCorrecaoPendente) return null;
          const mais: AcaoDoMenu[] = [];
          if (podeEstornar && m.transferenciaSituacao === null) mais.push({ rotulo: 'Estornar agora', aoEscolher: () => { abrir('estorno', m); } });
          if (podeExcluir) mais.push({ rotulo: 'Excluir lançamento', aoEscolher: () => { abrir('exclusao', m); } });
          return mais.length > 0 ? <MaisAcoes forma="icone" acoes={mais} /> : null;
        }}
        erro={
          consulta.isError ? (
            <EstadoVazio
              titulo="Não foi possível carregar os lançamentos"
              descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
              acao={
                <Botao
                  nivel="secundario"
                  onClick={() => {
                    void consulta.refetch();
                  }}
                >
                  Carregar os lançamentos
                </Botao>
              }
            />
          ) : null
        }
        vazio={
          <EstadoVazio
            titulo="Este tanque ainda não tem lançamento"
            descricao="Registre um recebimento ou peça um balanço e esta lista passa a mostrar cada entrada e cada saída, com a data e quem lançou."
          />
        }
      />

      {/* ------------------------------------------------ pedir a correção */}
      <Janela
        titulo="Pedir correção"
        aberta={acao === 'correcao' && alvo !== null}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
            <Botao
              nivel="primario"
              disabled={curto || !correcaoValida || corrigir.isPending}
              onClick={() => {
                if (alvo === null || correta === null) return;
                corrigir.mutate(
                  { id: alvo.id, envio: { quantidadeCorretaL: correta, motivo: motivo.trim() }, contexto: contextoDoLancamento(alvo) },
                  { onSuccess: fechar },
                );
              }}
            >
              {corrigir.isPending ? 'Pedindo…' : 'Pedir correção'}
            </Botao>
          </>
        }
      >
        {alvo === null ? null : (
          <div className="flex flex-col gap-4">
            {/* A recusa no TOPO do formulário (p.22). */}
            {corrigir.isError ? (
              <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(corrigir.error, 'O pedido não foi aceito')}</p>
            ) : null}
            <p>
              {rotuloDoLancamento(alvo.tipo)} de{' '}
              <strong className="tabular-nums">{formatarAteDuasCasas(Number(alvo.quantidadeL))} L</strong>, de{' '}
              {formatarDataHora(new Date(alvo.ocorridoEm))}. O pedido vai para quem tem alçada de ajuste; aprovado, a
              linha errada é anulada e a certa entra com a mesma data.
            </p>

            <CampoTexto
              rotulo="Quantidade certa, em litros"
              valor={quantidade}
              aoMudar={setQuantidade}
              placeholder="1.900"
              ajuda="Zero anula o lançamento inteiro."
              validar={(v) => {
                const n = interpretarNumero(v);
                if (n === null || n < 0) return 'Informe a quantidade certa em litros, ou zero para anular.';
                if (n === Number(alvo.quantidadeL)) return 'É a mesma quantidade já lançada. Informe o valor certo.';
                return null;
              }}
            />

            <CampoTexto
              rotulo="Por que este lançamento está errado"
              valor={motivo}
              aoMudar={setMotivo}
              maxLength={300}
              placeholder="Digitei 19000 em vez de 1900, um zero a mais"
              ajuda="Escreva o que houve, não só que houve: é o que quem aprova vai ler."
              validar={(v) =>
                v.trim().length < 15 ? 'Escreva o motivo com pelo menos 15 caracteres, dizendo o que aconteceu.' : null
              }
            />

          </div>
        )}
      </Janela>

      {/* ------------------------------------------- o estorno imediato (ADR-008) */}
      <Janela
        titulo="Estornar lançamento"
        aberta={acao === 'estorno' && alvo !== null}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Manter o lançamento
            </Botao>
            <Botao
              nivel="destrutivo"
              disabled={curto || estornar.isPending}
              onClick={() => {
                if (alvo === null) return;
                estornar.mutate(
                  { id: alvo.id, motivo: motivo.trim(), contexto: contextoDoLancamento(alvo) },
                  { onSuccess: fechar },
                );
              }}
            >
              {estornar.isPending ? 'Estornando…' : 'Estornar lançamento'}
            </Botao>
          </>
        }
      >
        {alvo === null ? null : (
          <div className="flex flex-col gap-4">
            {/* A recusa no TOPO do formulário (p.22). */}
            {estornar.isError ? (
              <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(estornar.error, 'O estorno não foi aceito')}</p>
            ) : null}
            <p>
              Estornar {rotuloDoLancamento(alvo.tipo)} de{' '}
              <strong className="tabular-nums">{formatarAteDuasCasas(Number(alvo.quantidadeL))} L</strong>, de{' '}
              {formatarDataHora(new Date(alvo.ocorridoEm))}, sem passar por aprovação? O volume do tanque volta ao que era
              antes dele, e as duas linhas ficam nos lançamentos.
            </p>

            <CampoTexto
              rotulo="Por que este lançamento está errado"
              valor={motivo}
              aoMudar={setMotivo}
              maxLength={300}
              placeholder="Digitei 19000 em vez de 1900, um zero a mais"
              ajuda="Escreva o que houve, não só que houve: é o que a conferência vai ler daqui a três meses."
              validar={(v) =>
                v.trim().length < 15
                  ? 'Escreva o motivo com pelo menos 15 caracteres, dizendo o que aconteceu.'
                  : null
              }
            />

          </div>
        )}
      </Janela>

      {/* ------------------------------------- excluir, só a plataforma (0059) */}
      <Janela
        titulo="Excluir lançamento"
        aberta={acao === 'exclusao' && alvo !== null}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>
              Manter o lançamento
            </Botao>
            <Botao
              nivel="destrutivo"
              disabled={curto || excluir.isPending}
              onClick={() => {
                if (alvo === null) return;
                excluir.mutate({ id: alvo.id, motivo: motivo.trim(), contexto: contextoDoLancamento(alvo) }, { onSuccess: fechar });
              }}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir lançamento'}
            </Botao>
          </>
        }
      >
        {alvo === null ? null : (
          <div className="flex flex-col gap-4">
            {/* A recusa no TOPO do formulário (p.22). */}
            {excluir.isError ? (
              <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(excluir.error, 'A exclusão não foi aceita')}</p>
            ) : null}
            <p>
              Excluir {rotuloDoLancamento(alvo.tipo)} de{' '}
              <strong className="tabular-nums">{formatarAteDuasCasas(Number(alvo.quantidadeL))} L</strong>, de{' '}
              {formatarDataHora(new Date(alvo.ocorridoEm))}? A linha sai dos lançamentos e o volume do tanque muda. Uma
              transferência sai inteira, das duas pontas. A linha, o motivo e o seu nome ficam no registro de exclusões.
            </p>

            <CampoTexto
              rotulo="Por que a linha vai sair dos lançamentos"
              valor={motivo}
              aoMudar={setMotivo}
              maxLength={300}
              placeholder="Lançamento de teste feito no ambiente do cliente"
              validar={(v) =>
                v.trim().length < 15 ? 'Escreva o motivo com pelo menos 15 caracteres.' : null
              }
            />

          </div>
        )}
      </Janela>
    </section>
  );
}

/**
 * A recusa do servidor já vem escrita para quem opera (`comoResolver`), e é
 * ela que a tela mostra. A frase genérica só entra quando não veio nada —
 * inventar causa seria transformar defeito de servidor em culpa de quem digitou.
 */
function mensagemDoErro(erro: unknown, padrao: string): string {
  const corpo = (erro as { corpo?: { erro?: string; comoResolver?: string } } | null)?.corpo;
  if (corpo?.comoResolver) return `${corpo.erro ?? padrao}. ${corpo.comoResolver}`;
  return `${padrao}: o pedido não chegou ao servidor. Tente outra vez; se continuar, avise o suporte com o horário.`;
}
